import { Button, Form, Tabs } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteExtension } from "src/business/extensions/apiExtensions";
import { ZExtension } from "src/business/extensions/ZExtension";
import { SysDataFormItems } from "src/business/sysData/SysDataFormItems";
import { onError } from "src/common/onError";
import { DeleteButtonStd } from "src/components/DeleteButtonStd/DeleteButtonStd";
import { IdBox } from "src/components/IdBox";
import { LoaderBox } from "src/components/LoaderBox";
import { msgQueueStore } from "src/components/MsgQueue";
import { PageLayout } from "src/components/PageLayout";
import { PageSubheader } from "src/components/PageSubheader";
import { ToolBox2 } from "src/components/ToolBox2";
import * as layoutStyles from "src/pages/management/objects/common/ObjectCardLayout.module.less";
import { ObjectCardTabPane } from "src/pages/management/objects/common/ObjectCardTabPane";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import {
  extensionCardPageStore as pageStore,
  ExtensionTabId,
  ZExtensionFormValues,
} from "./extensionCardPageStore";
import { ExtensionMainForm } from "./ExtensionMainForm";
import { mapExtensionToFormValues } from "./mapExtensionToFormValues";

export const ExtensionCardPage: React.FC = observer(() => {
  const { id = "" } = useParams();
  React.useEffect(() => {
    pageStore.init(id).catch(onError);
  }, [id]);
  return (
    <PageLayout title={pageStore.crumbs}>
      <LoaderBox
        remoteData={pageStore.data}
        drawReady={({ extension }) => <Inner extension={extension} />}
      />
    </PageLayout>
  );
});

const Inner: React.FC<{
  extension: ZExtension | null;
}> = observer(({ extension }) => {
  const { isNew, curTab } = pageStore;
  const [form] = Form.useForm<ZExtensionFormValues>();
  const navigate = useNavigate();
  const initialValues = React.useMemo(
    () => mapExtensionToFormValues(extension),
    [extension],
  );

  const doDelete = async () => {
    try {
      await deleteExtension(pageStore.extensionId);
      msgQueueStore.add({ type: "success", message: "Расширение удалено" });
      navigate(makeUrl(PageUrl.managementExtensions), { replace: true });
    } catch (error) {
      onError(error);
    }
  };

  const onSubmit = (values: ZExtensionFormValues) => {
    const onSuccessEdit = () => {
      msgQueueStore.add({ type: "success", message: "Расширение сохранено" });
    };
    const onSuccessNew = (resp: ZExtension) => {
      msgQueueStore.add({ type: "success", message: "Расширение создано" });
      navigate(makeUrl(PageUrl.managementExtensionCard, { id: resp.id }), {
        replace: true,
      });
    };
    const onSuccess = isNew ? onSuccessNew : onSuccessEdit;
    pageStore.save(values, onSuccess);
  };

  return (
    <>
      <PageSubheader title={pageStore.title}>
        {!isNew && (
          <DeleteButtonStd
            task={doDelete}
            content={`Запись "${extension?.name}" будет удалена без возможности восстановления.`}
          />
        )}
        <Button
          type="primary"
          htmlType="submit"
          onClick={() => form.submit()}
          loading={pageStore.saving}
        >
          {isNew ? "Создать" : "Сохранить"}
        </Button>
      </PageSubheader>

      <ToolBox2 paddingMode="page">
        <Tabs
          items={pageStore.tabOptions}
          activeKey={curTab}
          onChange={(key) => pageStore.setCurTab(key as ExtensionTabId)}
        />

        <ObjectCardTabPane
          hidden={curTab !== "main"}
          hiddenClassName={layoutStyles.hidden}
        >
          <ExtensionMainForm
            saving={pageStore.saving}
            form={form}
            idBox={<IdBox entity={extension ?? undefined} />}
            isNew={isNew}
            initialValues={initialValues}
            onSubmit={onSubmit}
          />
        </ObjectCardTabPane>
        {!isNew && (
          <ObjectCardTabPane
            className={layoutStyles.commonBlock}
            hidden={curTab !== "sys"}
            hiddenClassName={layoutStyles.hidden}
          >
            <SysDataFormItems data={extension ?? undefined} />
          </ObjectCardTabPane>
        )}
      </ToolBox2>
    </>
  );
});
